import { useState } from 'react'
import { api, ApiClientError } from '@/lib/api'
import { toastError, toastSuccess } from '@/store/toast'
import type { ProgressionNote, CreateProgressionNoteRequest, PainLocation } from '@yui-receipt/shared'
import BodyDiagram from './BodyDiagram'

type Props = {
  visitId: number
  existing: ProgressionNote | null
  onSaved: (note: ProgressionNote) => void
  patientInfo?: {
    nameTh: string
    hn: string
    dob: string | null
    heightCm: number | null
    weightKg: number | null
    sex: string | null
  } | null
  therapistName?: string | null
}

const VAS_FACES = [
  { score: 0, emoji: '😊', label: 'ไม่ปวด', color: '#22c55e' },
  { score: 1, emoji: '🙂', label: '', color: '#4ade80' },
  { score: 2, emoji: '😐', label: 'ปวดเล็กน้อย', color: '#86efac' },
  { score: 3, emoji: '😕', label: '', color: '#fde047' },
  { score: 4, emoji: '😣', label: 'ปวดปานกลาง', color: '#facc15' },
  { score: 5, emoji: '😖', label: '', color: '#f59e0b' },
  { score: 6, emoji: '😩', label: 'ปวดมาก', color: '#fb923c' },
  { score: 7, emoji: '😫', label: '', color: '#f97316' },
  { score: 8, emoji: '😭', label: 'ปวดมากที่สุด', color: '#ef4444' },
  { score: 9, emoji: '🤯', label: '', color: '#dc2626' },
  { score: 10, emoji: '😵', label: 'ทนไม่ไหว', color: '#991b1b' },
]

export default function ProgressionNoteForm({
  visitId,
  existing,
  onSaved,
  patientInfo,
  therapistName,
}: Props) {
  // Vitals
  const [bpSystolic, setBpSystolic] = useState(existing?.bpSystolic?.toString() ?? '')
  const [bpDiastolic, setBpDiastolic] = useState(existing?.bpDiastolic?.toString() ?? '')
  const [hr, setHr] = useState(existing?.hr?.toString() ?? '')
  const [spo2, setSpo2] = useState(existing?.spo2?.toString() ?? '')
  const [tempC, setTempC] = useState(existing?.tempC?.toString() ?? '')

  // Pain Scale
  const [vasScore, setVasScore] = useState<number | ''>(existing?.vasScore ?? '')

  // Clinical text fields
  const [travelHistory, setTravelHistory] = useState(existing?.travelHistory ?? '')
  const [diagnosis, setDiagnosis] = useState(existing?.diagnosis ?? '')
  const [chiefComplaint, setChiefComplaint] = useState(existing?.chiefComplaint ?? '')
  const [subjectiveExam, setSubjectiveExam] = useState(existing?.subjectiveExam ?? '')
  const [objectiveExam, setObjectiveExam] = useState(existing?.objectiveExam ?? '')
  const [assessment, setAssessment] = useState(existing?.assessment ?? '')
  const [planGoal, setPlanGoal] = useState(existing?.planGoal ?? '')
  const [treatment, setTreatment] = useState(existing?.treatment ?? '')
  const [noteText, setNoteText] = useState(existing?.noteText ?? '')

  // Temperature Sensation Test
  const [tempSensation, setTempSensation] = useState(existing?.tempSensation ?? '')

  // Body Diagram
  const [painLocations, setPainLocations] = useState<PainLocation[]>(
    existing?.painLocations ?? [],
  )

  // Therapist
  const [therapistSig, setTherapistSig] = useState(
    existing?.therapistSignature ?? therapistName ?? '',
  )

  const [saving, setSaving] = useState(false)

  async function handleSave() {
    setSaving(true)
    try {
      const body: CreateProgressionNoteRequest = {
        bpSystolic: bpSystolic === '' ? null : Number(bpSystolic),
        bpDiastolic: bpDiastolic === '' ? null : Number(bpDiastolic),
        hr: hr === '' ? null : Number(hr),
        spo2: spo2 === '' ? null : Number(spo2),
        tempC: tempC === '' ? null : Number(tempC),
        vasScore: vasScore === '' ? null : Number(vasScore),
        noteText: noteText.trim() || null,
        diagnosis: diagnosis.trim() || null,
        chiefComplaint: chiefComplaint.trim() || null,
        subjectiveExam: subjectiveExam.trim() || null,
        objectiveExam: objectiveExam.trim() || null,
        assessment: assessment.trim() || null,
        planGoal: planGoal.trim() || null,
        treatment: treatment.trim() || null,
        travelHistory: travelHistory.trim() || null,
        tempSensation: tempSensation || null,
        painLocations: painLocations.length > 0 ? painLocations : undefined,
        therapistSignature: therapistSig.trim() || null,
      }
      const res = await api<{ progressionNote: ProgressionNote }>(
        `/visits/${visitId}/progression-note`,
        { body },
      )
      toastSuccess('บันทึก Physical Therapy Record สำเร็จ')
      onSaved(res.progressionNote)
    } catch (err) {
      const msg = err instanceof ApiClientError ? err.message : 'บันทึกไม่สำเร็จ'
      toastError(msg)
    } finally {
      setSaving(false)
    }
  }

  // Age calculation
  const age = patientInfo?.dob ? calcAge(patientInfo.dob) : null

  return (
    <div className="space-y-5">
      <h2 className="text-lg font-semibold">Physical Therapy Record</h2>

      {/* ---- Patient Info Header ---- */}
      {patientInfo && (
        <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
            <div>
              <span className="text-slate-500">NAME: </span>
              <span className="font-medium">{patientInfo.nameTh}</span>
            </div>
            <div>
              <span className="text-slate-500">HN: </span>
              <span className="font-mono">{patientInfo.hn}</span>
            </div>
            <div>
              <span className="text-slate-500">เพศ: </span>
              <span>{patientInfo.sex === 'M' ? 'ชาย' : patientInfo.sex === 'F' ? 'หญิง' : '—'}</span>
            </div>
            {patientInfo.heightCm && (
              <div>
                <span className="text-slate-500">Height: </span>
                <span>{patientInfo.heightCm} cm</span>
              </div>
            )}
            {patientInfo.weightKg && (
              <div>
                <span className="text-slate-500">Weight: </span>
                <span>{patientInfo.weightKg} kg</span>
              </div>
            )}
            {age != null && (
              <div>
                <span className="text-slate-500">Age: </span>
                <span>{age} ปี</span>
              </div>
            )}
            {patientInfo.dob && (
              <div>
                <span className="text-slate-500">DOB: </span>
                <span>{patientInfo.dob}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ---- Vitals ---- */}
      <fieldset className="space-y-2">
        <legend className="font-semibold text-sm text-slate-700">Vital Signs</legend>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
          <div>
            <label className="label text-xs">BP (sys)</label>
            <input
              className="input text-sm"
              type="number"
              placeholder="120"
              value={bpSystolic}
              onChange={(e) => setBpSystolic(e.target.value)}
            />
          </div>
          <div>
            <label className="label text-xs">BP (dia)</label>
            <input
              className="input text-sm"
              type="number"
              placeholder="80"
              value={bpDiastolic}
              onChange={(e) => setBpDiastolic(e.target.value)}
            />
          </div>
          <div>
            <label className="label text-xs">HR</label>
            <input
              className="input text-sm"
              type="number"
              placeholder="72"
              value={hr}
              onChange={(e) => setHr(e.target.value)}
            />
          </div>
          <div>
            <label className="label text-xs">SpO2 %</label>
            <input
              className="input text-sm"
              type="number"
              placeholder="98"
              value={spo2}
              onChange={(e) => setSpo2(e.target.value)}
            />
          </div>
          <div>
            <label className="label text-xs">Temp °C</label>
            <input
              className="input text-sm"
              type="number"
              step="0.1"
              placeholder="36.5"
              value={tempC}
              onChange={(e) => setTempC(e.target.value)}
            />
          </div>
          <div>
            <label className="label text-xs">RR</label>
            <input
              className="input text-sm"
              type="number"
              placeholder="—"
              disabled
              title="ยังไม่รองรับ"
            />
          </div>
        </div>
      </fieldset>

      {/* ---- Travel History ---- */}
      <div>
        <label className="label">ประวัติการเดินทางไปต่างประเทศ (Travel History)</label>
        <input
          className="input"
          placeholder="ระบุประเทศ, วันที่, หรือ 'ไม่มี'"
          value={travelHistory}
          onChange={(e) => setTravelHistory(e.target.value)}
        />
      </div>

      {/* ---- Diagnosis ---- */}
      <div>
        <label className="label">Diagnosis</label>
        <textarea
          className="input min-h-[60px]"
          placeholder="การวินิจฉัย..."
          value={diagnosis}
          onChange={(e) => setDiagnosis(e.target.value)}
        />
      </div>

      {/* ---- Chief Complaint ---- */}
      <div>
        <label className="label">Chief Complaint</label>
        <textarea
          className="input min-h-[60px]"
          placeholder="อาการสำคัญที่มาพบ..."
          value={chiefComplaint}
          onChange={(e) => setChiefComplaint(e.target.value)}
        />
      </div>

      {/* ---- Subjective Examination ---- */}
      <div>
        <label className="label">Subjective Examination</label>
        <textarea
          className="input min-h-[100px]"
          placeholder="ประวัติจากการซักถาม, อาการ, ปัจจัยที่ทำให้แย่ลง/ดีขึ้น..."
          value={subjectiveExam}
          onChange={(e) => setSubjectiveExam(e.target.value)}
        />
      </div>

      {/* ---- Objective Examination ---- */}
      <div>
        <label className="label">Objective Examination</label>
        <textarea
          className="input min-h-[100px]"
          placeholder="ผลการตรวจร่างกาย, ROM, MMT, Special Tests..."
          value={objectiveExam}
          onChange={(e) => setObjectiveExam(e.target.value)}
        />
      </div>

      {/* ---- Body Diagram ---- */}
      <BodyDiagram
        locations={painLocations}
        onChange={setPainLocations}
      />

      {/* ---- Pain Scale (Visual) ---- */}
      <fieldset className="space-y-2">
        <legend className="font-semibold text-sm text-slate-700">
          Pain Scale (VAS 0-10)
        </legend>
        <div className="flex flex-wrap gap-1 justify-center">
          {VAS_FACES.map((f) => (
            <button
              key={f.score}
              type="button"
              className={
                'flex flex-col items-center rounded-lg px-1.5 py-1 min-w-[32px] transition border-2 ' +
                (vasScore === f.score
                  ? 'border-brand-600 bg-brand-50 shadow-sm scale-110'
                  : 'border-transparent hover:bg-slate-50')
              }
              onClick={() => setVasScore(vasScore === f.score ? '' : f.score)}
              title={`${f.score} — ${f.label || f.emoji}`}
            >
              <span className="text-lg leading-none">{f.emoji}</span>
              <span
                className="text-[10px] font-bold mt-0.5"
                style={{ color: f.color }}
              >
                {f.score}
              </span>
            </button>
          ))}
        </div>
        {vasScore !== '' && (
          <p className="text-center text-sm">
            คะแนนปวด: <strong>{vasScore}/10</strong>{' '}
            {VAS_FACES[vasScore]?.label && (
              <span className="text-slate-500">({VAS_FACES[vasScore].label})</span>
            )}
          </p>
        )}
      </fieldset>

      {/* ---- Temperature Sensation Test ---- */}
      <fieldset className="space-y-2">
        <legend className="font-semibold text-sm text-slate-700">
          Temperature Sensation Test
        </legend>
        <div className="flex gap-2 flex-wrap">
          {[
            { value: 'hot', label: '🔴 Hot', desc: 'รู้สึกร้อน' },
            { value: 'cold', label: '🔵 Cold', desc: 'รู้สึกเย็น' },
            { value: 'normal', label: '🟢 Normal', desc: 'ปกติ' },
          ].map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={
                'flex-1 min-w-[80px] rounded-lg border-2 px-3 py-2 text-sm font-medium transition ' +
                (tempSensation === opt.value
                  ? 'border-brand-600 bg-brand-50 text-brand-700'
                  : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50')
              }
              onClick={() =>
                setTempSensation(tempSensation === opt.value ? '' : opt.value)
              }
            >
              <div>{opt.label}</div>
              <div className="text-[10px] text-slate-400">{opt.desc}</div>
            </button>
          ))}
        </div>
      </fieldset>

      {/* ---- Assessment ---- */}
      <div>
        <label className="label">Assessment</label>
        <textarea
          className="input min-h-[80px]"
          placeholder="การประเมินผล, clinical reasoning..."
          value={assessment}
          onChange={(e) => setAssessment(e.target.value)}
        />
      </div>

      {/* ---- Plan/Goal ---- */}
      <div>
        <label className="label">Plan / Goal</label>
        <textarea
          className="input min-h-[80px]"
          placeholder="เป้าหมายการรักษา, แผนระยะสั้น/ยาว..."
          value={planGoal}
          onChange={(e) => setPlanGoal(e.target.value)}
        />
      </div>

      {/* ---- Treatment ---- */}
      <div>
        <label className="label">Treatment</label>
        <textarea
          className="input min-h-[80px]"
          placeholder="การรักษาที่ให้วันนี้, modalities, exercises..."
          value={treatment}
          onChange={(e) => setTreatment(e.target.value)}
        />
      </div>

      {/* ---- Additional Note ---- */}
      <div>
        <label className="label">หมายเหตุเพิ่มเติม</label>
        <textarea
          className="input min-h-[60px]"
          placeholder="บันทึกเพิ่มเติม (ถ้ามี)"
          value={noteText}
          onChange={(e) => setNoteText(e.target.value)}
        />
      </div>

      {/* ---- Therapist Signature ---- */}
      <div>
        <label className="label">Physical Therapist (ผู้บันทึก)</label>
        <input
          className="input"
          placeholder="ชื่อนักกายภาพบำบัด"
          value={therapistSig}
          onChange={(e) => setTherapistSig(e.target.value)}
        />
      </div>

      {/* ---- Submit ---- */}
      <button
        className="btn-primary w-full min-h-tap"
        onClick={handleSave}
        disabled={saving}
      >
        {saving ? 'กำลังบันทึก...' : 'บันทึก Physical Therapy Record'}
      </button>
    </div>
  )
}

function calcAge(dob: string): number | null {
  const d = new Date(dob)
  if (isNaN(d.getTime())) return null
  const now = new Date()
  let age = now.getFullYear() - d.getFullYear()
  const m = now.getMonth() - d.getMonth()
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--
  return age
}
